/**
 * MyRingCentralConnect — per-rep RingCentral OAuth UI (Bundle 2, Feb 2026).
 *
 * Renders inside Settings → Integrations beneath the tenant-level RC card.
 * Each rep clicks "Connect" → popup opens the RC consent screen → on success
 * the popup posts a message back here and self-closes, triggering a status
 * refresh. When connected we show owner email + Disconnect.
 *
 * Without a connected OAuth token, per-rep dialing endpoints
 * (/api/integrations/rc/sms/send, /calls/make, /webphone/provision) return
 * `rc_oauth_required` so reps cannot dial from the shared JWT account.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Phone, CheckCircle2, Loader2, RefreshCw, LogOut } from "lucide-react";

export default function MyRingCentralConnect({ token, role }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const h = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/integrations/ringcentral/oauth/status`, { headers: h });
      setStatus(r.data);
    } catch {
      setStatus({ configured: false, connected: false });
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Listen for the popup `postMessage` callback so we refresh status the
  // instant the rep finishes the consent flow (no manual reload needed).
  useEffect(() => {
    const onMsg = (ev) => {
      const d = ev?.data;
      if (!d || typeof d !== "object" || d.type !== "rc-oauth") return;
      if (d.success) {
        toast.success("RingCentral connected");
        fetchStatus();
      } else {
        toast.error(d.message || "RingCentral connection failed");
      }
      setConnecting(false);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [fetchStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/integrations/ringcentral/oauth/url`, { headers: h });
      if (!r.data?.ready) {
        toast.error(r.data?.message || "Per-rep OAuth is not configured.");
        setConnecting(false);
        return;
      }
      const w = window.open(r.data.url, "rc-oauth", "width=520,height=720,left=200,top=120");
      if (!w) {
        toast.error("Popup blocked. Allow popups for this site and try again.");
        setConnecting(false);
        return;
      }
      // Safety net: if popup closes without posting back (rep hits Cancel),
      // reset the spinner after a generous timeout.
      const guard = setInterval(() => {
        if (w.closed) {
          clearInterval(guard);
          setConnecting(false);
          fetchStatus();
        }
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start RingCentral OAuth.");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/integrations/ringcentral/oauth/disconnect`, {}, { headers: h });
      toast.success("RingCentral disconnected");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ padding: 16, marginTop: 14, display: "flex", alignItems: "center", gap: 10 }} data-testid="my-rc-connect-loading">
        <Loader2 style={{ width: 14, height: 14, color: "#888", animation: "spin 1s linear infinite" }} />
        <span style={{ fontSize: 12, color: "#888" }}>Loading your RingCentral status…</span>
      </div>
    );
  }

  if (!status?.configured) {
    // Iter 235 (July 2026) — admin-only hint when per-rep OAuth
    // isn't wired at the platform level. Regular reps keep the
    // panel hidden (unchanged).
    const isAdmin = ["super_admin", "crm_admin"].includes(role);
    if (!isAdmin) return null;
    return (
      <div
        className="card"
        data-testid="my-rc-not-configured-admin"
        style={{
          padding: "12px 14px",
          marginTop: 14,
          background: "#FFFBEB",
          border: "0.5px solid #FDE68A",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Phone style={{ width: 14, height: 14, color: "#B45309" }} />
          <div style={{ fontSize: 12, fontWeight: 600, color: "#78350F" }}>
            Per-rep RingCentral OAuth not configured on this deployment
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#78350F", marginTop: 6, lineHeight: 1.5 }}>
          Reps can&apos;t connect their personal RC accounts because the OAuth
          env vars aren&apos;t set. Reps will still be able to make calls via
          the tenant JWT connection, but concurrent dialing + per-rep call
          logs / recordings require per-rep OAuth. Set&nbsp;
          <code style={{ background: "#FEF3C7", padding: "1px 4px", borderRadius: 3 }}>RC_OAUTH_CLIENT_ID</code>,&nbsp;
          <code style={{ background: "#FEF3C7", padding: "1px 4px", borderRadius: 3 }}>RC_OAUTH_CLIENT_SECRET</code>,&nbsp;
          <code style={{ background: "#FEF3C7", padding: "1px 4px", borderRadius: 3 }}>RC_OAUTH_REDIRECT_URI</code>
          &nbsp;in backend/.env and restart the backend.
        </div>
      </div>
    );
  }

  const connected = !!status?.connected;

  return (
    <div className="card" style={{ padding: "18px 22px", marginTop: 14 }} data-testid="my-rc-connect-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Phone style={{ width: 16, height: 16, color: "#FF6A00" }} />
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>My RingCentral Account</h3>
        {connected && (
          <span style={{ padding: "2px 10px", borderRadius: 10, fontSize: 10, fontWeight: 600, background: "#DAFBE8", color: "#1D9E75", marginLeft: 4 }} data-testid="my-rc-connected-badge">
            Connected
          </span>
        )}
      </div>

      {connected ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 4 }}>
              You can now place outbound calls and send SMS from your own
              RingCentral extension and DIDs. Inbound calls to your DID will
              ring the in-browser softphone.
            </div>
            <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>
              <span style={{ fontWeight: 600 }}>Owner:</span>{" "}
              <span data-testid="my-rc-owner-email">{status?.owner_email || status?.owner_name || "—"}</span>
              {status?.extension_id ? <span style={{ marginLeft: 8 }}>· ext <span style={{ fontFamily: "monospace" }} data-testid="my-rc-extension-id">{status.extension_id}</span></span> : null}
            </div>
            {status?.connected_at && (
              <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 4 }}>
                Connected {new Date(status.connected_at).toLocaleString()}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleConnect}
              disabled={connecting}
              data-testid="my-rc-reconnect-btn"
              style={{ background: "#fff", border: "0.5px solid var(--border)", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: connecting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, color: "#334155", fontFamily: "inherit" }}
            >
              <RefreshCw style={{ width: 12, height: 12 }} /> Reconnect
            </button>
            <button
              onClick={handleDisconnect}
              data-testid="my-rc-disconnect-btn"
              style={{ background: "#fff", border: "0.5px solid #ffccc7", borderRadius: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, color: "#A32D2D", fontFamily: "inherit" }}
            >
              <LogOut style={{ width: 12, height: 12 }} /> Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 4 }}>
              Connect your personal RingCentral account so you can dial from
              your own caller-ID, send SMS, and answer inbound calls in the
              browser softphone. Each rep needs to do this once.
            </div>
            <div style={{ fontSize: 11, color: "#92400e", background: "#fffbeb", border: "0.5px solid #fcd34d", borderRadius: 6, padding: "6px 8px", marginTop: 6 }}>
              Until connected, the Sales Dialer and Communications softphone
              will return "Please connect your RingCentral in Settings".
            </div>
          </div>
          <button
            onClick={handleConnect}
            disabled={connecting}
            data-testid="my-rc-connect-btn"
            style={{ background: "#2563eb", color: "#fff", border: 0, borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 600, cursor: connecting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit", whiteSpace: "nowrap" }}
          >
            {connecting ? (
              <><Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> Connecting…</>
            ) : (
              <><CheckCircle2 style={{ width: 13, height: 13 }} /> Connect my RingCentral</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
